import { useCallback } from "react";
import {
  useDeleteMutation,
  useGetQuery,
  useLazyGetQuery,
  usePostMutation,
  usePutMutation,
} from "slices/api";
import { USER, USER_MANAGEMENT, UTILITY_CONSUMER } from "tagTypes";

export const useGetUsersQuery = (query = {}, isFireSafety = false) =>
  useGetQuery({
    endpoint: isFireSafety ? "/firesafety-user" : "/user",
    query,
    tags: [USER],
  });

export const useGetUserApproverDetails = (id) =>
  useGetQuery({
    endpoint: `/digital-signature-approver/${id}`,
    tags: [USER_MANAGEMENT],
  });

export const useGetLazyGetUserApproverDetails = () => {
  const [approverDetails, data] = useLazyGetQuery();
  const getApproverDetails = async (id) => {
    return await approverDetails(
      {
        endpoint: `/digital-signature-approver/${id}`,
        tags: [USER_MANAGEMENT],
      },
      true
    ).unwrap();
  };
  return [getApproverDetails, data];
};

export const useGetSignatureApproverUsers = (query = {}) =>
  useGetQuery({
    endpoint: "/user/approvers",
    query,
    tags: [USER_MANAGEMENT],
  });

export const useConsumersQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/user/utility-consumer",
    query,
    tags: [USER],
  });

export const useGetLazyConsumerNumbersQuery = () => {
  const [getConsumerNumbers, data] = useLazyGetQuery();
  const consumerNumbers = async (query = {}) => {
    return await getConsumerNumbers(
      {
        endpoint: "/user/utility-consumer",
        query,
        tags: [USER],
      },
      true
    ).unwrap();
  };
  return [consumerNumbers, data];
};

export const useLazyLoginUserQuery = () => {
  const [loginUser, data] = useLazyGetQuery();
  const login = async (query = {}) => {
    return await loginUser(
      {
        endpoint: "/user",
        query,
        tags: [USER],
      },
      true
    ).unwrap();
  };
  return [login, data];
};

export const useGetConsumerDataByUtilityConsumerId = (userId, query = {}) =>
  useGetQuery({
    endpoint: `/power/utility-consumer/${userId}`,
    query,
    tags: [USER, UTILITY_CONSUMER],
  });

export const useLazyConsumerByIdQuery = () => {
  const [consumerById, data] = useLazyGetQuery();
  const consumerData = async (id) => {
    return await consumerById({
      endpoint: `/power/utility-consumer/${id}`,
      tags: [USER],
    }).unwrap();
  };
  return [consumerData, data];
};

export const useLazyGetOtherConsumerByIdQuery = () => {
  const [consumerById, data] = useLazyGetQuery();
  const consumerData = async (id) => {
    return await consumerById({
      endpoint: `/other-utility-consumer/${id}`,
      tags: [USER],
    }).unwrap();
  };
  return [consumerData, data];
};
export const useLazyGetOtherConsumerByConsumerNumber = () => {
  const [getConsumerData, data] = useLazyGetQuery();
  const consumerData = async (query = {}) => {
    return await getConsumerData({
      endpoint: "/user/other-utility-consumer",
      query,
      tags: [USER],
    }).unwrap();
  };
  return [consumerData, data];
};

export const useGetUserQuery = (userId, query = {}) =>
  useGetQuery({
    endpoint: `/user/${userId}`,
    query,
    tags: [USER],
  });

export const useLazyGetUserQuery = () => {
  const [get, data] = useLazyGetQuery();
  const getUserData = useCallback(
    async (userId, query = {}, isFireSafety = false) => {
      return await get({
        endpoint: `/${isFireSafety ? "firesafety-user" : "user"}/${userId}`,
        query,
        tags: [USER],
      }).unwrap();
    },
    [get]
  );
  return [getUserData, data];
};

export const useGetAuthorizedPersonQuery = (query = {}) =>
  useGetQuery({
    endpoint: "/user/authorized-person-detail",
    query,
    tags: [USER],
  });

export const useLazyGetAuthorizedPersonQuery = () => {
  const [loginUser, data] = useLazyGetQuery();
  const login = useCallback(
    async (query = {}) =>
      await loginUser(
        {
          endpoint: "/user/authorized-person-detail",
          query,
          tags: [USER],
        },
        true
      ).unwrap(),
    [loginUser]
  );
  return [login, data];
};

export const usePostLoginMutation = () => {
  const [loginUser, data] = usePostMutation();
  const login = async (payload, query = {}) => {
    return await loginUser({
      endpoint: "/user/login",
      query,
      payload: payload || {},
      tags: [USER],
    }).unwrap();
  };

  return [login, data];
};

export const usePutUploadDigitalSignatureMutation = () => {
  const [uploadDigitalSignature, data] = usePutMutation();
  const uploadSignature = useCallback(
    async (payload, isFireSafety = false) => {
      return await uploadDigitalSignature({
        endpoint: isFireSafety
          ? "/firesafety-user/upload-signature"
          : "/user/upload-signature",
        payload: payload || {},
        toastMessage: "Digital signature uploaded successfully",
        tags: [USER],
      }).unwrap();
    },
    [uploadDigitalSignature]
  );

  return [uploadSignature, data];
};
export const usePostAdminRegisterMutation = () => {
  const [registerUser, data] = usePostMutation();
  const register = async (payload, isFireSafety = false) => {
    return await registerUser({
      endpoint: isFireSafety
        ? "/firesafety-user/register-user "
        : "/register-user",
      payload,
      toastMessage: "User added successfully",
      tags: [USER],
    }).unwrap();
  };
  return [register, data];
};

export const usePostRegisterMutation = () => {
  const [registerUser, data] = usePostMutation();
  const register = async (payload, query = {}) => {
    return await registerUser({
      endpoint: "/register-consumer",
      query,
      payload: payload || {},
      toastMessage: "Registered successfully",
      tags: [USER],
    }).unwrap();
  };
  return [register, data];
};

export const usePostUserApproverMutation = () => {
  const [userApprover, data] = usePostMutation();
  const addUserApprover = async ({ payload, query = {}, toastMessage }) => {
    return await userApprover({
      endpoint: "/digital-signature-approver",
      query,
      payload: payload || {},
      toastMessage,
      tags: [USER_MANAGEMENT],
    }).unwrap();
  };
  return [addUserApprover, data];
};

export const usePutAdminProfileMutation = () => {
  const [handleUser, data] = usePutMutation();
  const updateAdminProfileData = async (
    payload,
    query = {},
    isFireSafety = false
  ) => {
    return await handleUser({
      endpoint: isFireSafety ? "/firesafety-user" : "/user",
      query,
      payload: payload || {},
      toastMessage: "User data updated successfully",
      tags: [USER],
    }).unwrap();
  };
  return [updateAdminProfileData, data];
};

export const usePutConsumerProfileMutation = () => {
  const [handleUser, data] = usePutMutation();
  const updateConsumerProfileData = async (payload, isFireSafety = false) => {
    return await handleUser({
      endpoint: isFireSafety ? "/firesafety-user/consumer" : "/user/consumer",
      payload: payload || {},
      toastMessage: "User data updated successfully",
      tags: [USER],
    }).unwrap();
  };
  return [updateConsumerProfileData, data];
};

export const usePutChangeConsumerPasswordMutation = () => {
  const [changePassword, data] = usePutMutation();
  const updatePassword = async (payload, isFireSafety = false) => {
    return await changePassword({
      endpoint: isFireSafety
        ? "/firesafety-user/update-password"
        : "/user/update-password",
      payload: payload || {},
      toastMessage: "Password changed successfully",
      tags: [USER],
    }).unwrap();
  };
  return [updatePassword, data];
};

export const useDeleteUserMutation = () => {
  const [handleUser, data] = useDeleteMutation();
  const handleUserData = async (userId, isFireSafety = false) => {
    return await handleUser({
      endpoint: isFireSafety ? `/firesafety-user/${userId}` : `/user/${userId}`,
      toastMessage: "User deleted successfully",
      tags: [USER],
    }).unwrap();
  };
  return [handleUserData, data];
};

export const useDeleteUserApproverMutation = () => {
  const [handleDeleteUserApprover, data] = useDeleteMutation();
  const deleteApprover = async (id) => {
    return await handleDeleteUserApprover({
      endpoint: `/digital-signature-approver/${id}`,
      toastMessage: "Approver deleted successfully",
      tags: [USER_MANAGEMENT],
    }).unwrap();
  };
  return [deleteApprover, data];
};
